import React, { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import useFetchContestDetails from '../hooks/useFetchContestDetails';
import ContestDetails from '../details/ContestDetails';
import EntrantsTeamsPokerRunning from './EntrantsTeams/EntrantsTeamsPokerRunning';
import SelectedTeamPokerRunning from './SelectedTeam/SelectedTeamPokerRunning';
import EntrantsTeamsGolfRunning from './EntrantsTeams/EntrantsTeamsGolfRunning';
import SelectedTeamGolfRunning from './SelectedTeam/SelectedTeamGolfRunning';
import EntrantsTeamsChessRunning from './EntrantsTeams/EntrantsTeamsChessRunning';
import SelectedTeamChessRunning from './SelectedTeam/SelectedTeamChessRunning';
import EntrantsTeamsEsportsRunning from './EntrantsTeams/EntrantsTeamsEsportsRunning';
import SelectedTeamEsportsRunning from './SelectedTeam/SelectedTeamEsportsRunning';
import BackButton from '../components/general/BackButton';
import ViewOwnershipModal from '../details/modals/ViewOwnershipModal';
import '../components/ContestInfo.css';

const ContestInfoRunning = () => {
  const { id } = useParams();
  const { contest, loading, error, refetch } = useFetchContestDetails(id);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const selectedTeamRef = useRef(null);
  const [isAutoRefreshEnabled, setIsAutoRefreshEnabled] = useState(false);
  const [refreshError, setRefreshError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [showOwnershipModal, setShowOwnershipModal] = useState(false);
  const MAX_RETRIES = 3;

  // Add retry effect
  useEffect(() => {
    if (error && retryCount < MAX_RETRIES) {
      const retryTimer = setTimeout(async () => {
        try {
          await refetch();
          setRetryCount(prev => prev + 1);
        } catch (err) {
          console.error('Retry failed:', err);
        }
      }, 2000); // Wait 2 seconds between retries

      return () => clearTimeout(retryTimer);
    }
  }, [error, retryCount, refetch]);

  useEffect(() => {
    if (!isAutoRefreshEnabled) return;

    const refreshInterval = setInterval(async () => {
      try {
        setRefreshError(null);
        await refetch();
      } catch (err) {
        console.error('Auto-refresh failed:', err);
        setRefreshError(err.message || 'Failed to refresh data');
        // Disable auto-refresh on error
        setIsAutoRefreshEnabled(false);
      }
    }, 60000);

    return () => clearInterval(refreshInterval);
  }, [refetch, isAutoRefreshEnabled]);

  useEffect(() => {
    if (selectedTeam && selectedTeamRef.current) {
      selectedTeamRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }, [selectedTeam]);

  if (loading) {
    return (
      <div className="contest-loading-info">
        Loading Contest Information...
      </div>
    );
  }

  if (error && retryCount >= MAX_RETRIES) {
    return (
      <div className="contest-loading-info">
        Error: {error.message || 'Could not load contest details.'}
        <button 
          onClick={() => {
            setRetryCount(0);
            refetch();
          }}
          className="retry-button"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!contest) {
    return (
      <div className="contest-loading-info">
        Loading Contest Information...
        {retryCount > 0 && <div className="retry-status">Retrying... ({retryCount}/{MAX_RETRIES})</div>}
      </div>
    );
  }

  const currentUsername = localStorage.getItem('username');

  let EntrantsTeamsComponent;
  let SelectedTeamComponent;

  switch (contest.sport) {
    case 'Golf':
      EntrantsTeamsComponent = EntrantsTeamsGolfRunning;
      SelectedTeamComponent = SelectedTeamGolfRunning;
      break;
    case 'Chess':
      EntrantsTeamsComponent = EntrantsTeamsChessRunning;
      SelectedTeamComponent = SelectedTeamChessRunning;
      break;
    case 'Esports':
      EntrantsTeamsComponent = EntrantsTeamsEsportsRunning;
      SelectedTeamComponent = SelectedTeamEsportsRunning;
      break;
    case 'Poker':
    default:
      EntrantsTeamsComponent = EntrantsTeamsPokerRunning;
      SelectedTeamComponent = SelectedTeamPokerRunning;
      break;
  }

  return (
    <div className="contest-details-container">
      <BackButton to="/contests/running">
        ◄ Back to Running Contests
      </BackButton>
      {contest && <ContestDetails contest={contest} showEnterButton={false} />}
      <EntrantsTeamsComponent
        contest={contest}
        currentUsername={currentUsername}
        onTeamSelect={setSelectedTeam}
        onRefreshData={refetch}
        isAutoRefreshEnabled={isAutoRefreshEnabled}
        onToggleAutoRefresh={() => setIsAutoRefreshEnabled(!isAutoRefreshEnabled)}
        refreshError={refreshError}
      />
      <div ref={selectedTeamRef}>
        {selectedTeam && (
          <SelectedTeamComponent
            team={selectedTeam}
            contest={contest}
          />
        )}
      </div>
      <div className="tiebreaker-info">
        * Captain's score counts as tiebreaker
      </div>
      <ViewOwnershipModal
        show={showOwnershipModal}
        onClose={() => setShowOwnershipModal(false)}
        players={contest.players}
        entrants={contest.entrants}
        currentUsername={currentUsername}
        contest={contest}
      />
    </div>
  );
};

export default ContestInfoRunning;
